import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, ArrowUpRight, RefreshCw } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Hero } from '@/components/common/Hero';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useBlogPosts, useBlogCategories } from '@/hooks/useBlog';
import { useTranslation } from '@/features/i18n';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

export function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useBlogPosts({ category: selectedCategory, page, limit: 9 });
  const { data: categories } = useBlogCategories();
  const { t } = useTranslation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return (
      <>
        <SEO title={t.blog.title} />
        <Hero
          title={t.blog.title}
          subtitle={t.blog.subtitle}
          description={t.blog.description}
          size="md"
          variant="light"
        />
        <section className="ninito-section bg-[#edf1f5]">
          <div className="ninito-container">
            <EmptyState
              title={t.common?.error || 'Error loading data'}
              description={t.common?.tryAgain || 'Please try again later'}
              action={
                <button
                  onClick={() => refetch()}
                  className="ninito-btn ninito-btn-primary mt-4"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t.common?.retry || 'Retry'}
                </button>
              }
            />
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title={t.blog.title}
        description={t.blog.seoDescription}
      />

      <Hero
        title={t.blog.title}
        subtitle={t.blog.subtitle}
        description={t.blog.description}
        size="md"
        variant="light"
      />

      <section className="ninito-section bg-[#edf1f5]">
        <div className="ninito-container">
          {/* Category filters - ninito pill style */}
          {categories && categories.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-12">
              <button
                onClick={() => {
                  setSelectedCategory(undefined);
                  setPage(1);
                }}
                className={cn(
                  'px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200',
                  !selectedCategory
                    ? 'bg-black text-white'
                    : 'bg-white text-black/70 hover:bg-black/5 border border-black/10'
                )}
              >
                {t.blog.all}
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setPage(1);
                  }}
                  className={cn(
                    'px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200',
                    selectedCategory === category
                      ? 'bg-black text-white'
                      : 'bg-white text-black/70 hover:bg-black/5 border border-black/10'
                  )}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {/* Blog posts grid - ninito card style */}
          {data?.posts && data.posts.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
                {data.posts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Link to={`/blog/${post.slug}`} className="block h-full group">
                      <div className="ninito-card h-full flex flex-col overflow-hidden p-0 ninito-hover-lift">
                        {post.cover_image_url && (
                          <div className="relative aspect-[16/10] overflow-hidden rounded-t-[28px]">
                            <img
                              src={post.cover_image_url}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="p-6 flex flex-col flex-1">
                          {post.category && (
                            <span className="inline-flex items-center self-start px-3 py-1 rounded-full bg-[#0078ff]/10 text-[#0078ff] text-xs font-medium mb-4">
                              {post.category}
                            </span>
                          )}
                          <h2 className="text-xl font-semibold text-black tracking-tight group-hover:text-[#0078ff] transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                          <p className="mt-3 text-[#666] leading-relaxed flex-1 line-clamp-3">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-sm text-[#999] pt-5 mt-5 border-t border-black/10">
                            <div className="flex items-center gap-4">
                              <span className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1.5" />
                                {post.published_at ? formatDate(post.published_at) : t.blog.draft}
                              </span>
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1.5" />
                                {post.reading_time} min
                              </span>
                            </div>
                            <ArrowUpRight className="w-5 h-5 text-[#0078ff] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>

              {/* Pagination - ninito style */}
              {data.totalPages > 1 && (
                <div className="mt-16 flex justify-center items-center gap-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="ninito-btn ninito-btn-outline disabled:opacity-50"
                  >
                    {t.blog.previous}
                  </button>
                  <span className="text-[#666] text-sm">
                    {t.blog.pageOf.replace('{page}', String(page)).replace('{total}', String(data.totalPages))}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                    disabled={page === data.totalPages}
                    className="ninito-btn ninito-btn-outline disabled:opacity-50"
                  >
                    {t.blog.next}
                  </button>
                </div>
              )}
            </>
          ) : (
            <EmptyState
              title={t.blog.noPosts}
              description={
                selectedCategory
                  ? `${t.blog.noPostsCategory} "${selectedCategory}".`
                  : t.blog.noPostsYet
              }
              action={
                selectedCategory && (
                  <button
                    className="ninito-btn ninito-btn-outline"
                    onClick={() => setSelectedCategory(undefined)}
                  >
                    {t.blog.viewAll}
                  </button>
                )
              }
            />
          )}
        </div>
      </section>

      {/* Newsletter CTA - ninito dark style */}
      <section className="ninito-cta">
        <div className="relative ninito-container">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-white tracking-[-0.02em]"
          >
            {t.blog.newsletter.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-5 text-lg md:text-xl text-[#999] max-w-[600px] mx-auto"
          >
            {t.blog.newsletter.description}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-10"
          >
            <Link
              to="/contact"
              className="ninito-btn ninito-btn-white ninito-btn-lg group"
            >
              {t.common.contactUs}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
